import type { Space } from '@cocoonspace/sdk-js/types/space'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Calendar, X } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { Price } from '~/components/price'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import type { TimeSlot } from '../templates/booking-stepper'

interface DetailsSidebarStepStartProps {
	className?: string
	space: Space
	booking: TimeSlot
	onClick: () => void
	resetBooking: () => void
}

export const DetailsSidebarStepStart = ({
	className,
	space,
	booking,
	onClick,
	resetBooking,
}: DetailsSidebarStepStartProps) => {
	const { t } = useTranslation()

	const humanReadableDate = useMemo(() => {
		if (booking && booking.day && booking.start && booking.end) {
			const day = dateTimeUtils(booking.day).format('D MMM')
			const start = booking.start?.replace('-', 'h')
			const end = booking.end?.replace('-', 'h')

			return `${day} de ${start} à ${end}`
		} else {
			return ''
		}
	}, [booking]) // eslint-disable-line react-hooks/exhaustive-deps

	const bookingMinDuration = dateTimeUtils
		.duration(space.min_minutes || 60, 'minutes')
		.format('H[h]mm')
		.replace('h00', 'h')

	return (
		<div>
			<div className={cn('p-4 max-md:pb-0', className)}>
				<div className='flex flex-row justify-between text-xl'>
					<div className='flex items-end gap-x-1 leading-none'>
						{space.prices?.weekday_min?.hour_standard !==
							space.prices?.weekday_max?.hour_standard && (
							<div className='mb-0.5 text-sm'>{t('common:from')}</div>
						)}

						<Price
							price={
								space.prices?.weekday_min?.hour_discounted ??
								space.prices?.weekday_min?.hour_standard
							}
							suffix='hourlyLong'
							suffixClassName='text-xs font-normal'
							taxType='ht'
						/>
					</div>

					<Price
						price={
							space.prices?.weekday_min?.day_discounted ??
							space.prices?.weekday_min?.day_standard
						}
						suffix='dailyLong'
						suffixClassName='text-xs'
						taxType='ht'
					/>
				</div>
			</div>

			<hr className='border-slate-200 max-md:hidden' />

			<div className='flex flex-col gap-2 p-4 md:pt-6'>
				<div className='flex flex-row items-center gap-2 rounded border p-2 text-foreground/60'>
					<button
						type='button'
						className='flex flex-1 flex-row items-center gap-2'
						onClick={onClick}
					>
						<Calendar
							size={22}
							className='text-primary'
							strokeWidth={1.5}
						/>

						<div className='flex-1 text-left text-sm'>
							{humanReadableDate || 'Votre créneau'}
						</div>
					</button>

					{!!humanReadableDate && (
						<button
							type='button'
							onClick={() => resetBooking()}
						>
							<X
								size={16}
								className='text-primary'
							/>
						</button>
					)}
				</div>

				<div className='mb-4 text-center text-slate-500 text-sm max-md:hidden'>
					{t('booking:stepper.firstStep.create.info', {
						duration: bookingMinDuration,
					})}
				</div>

				<Button
					data-testid='select-time-slot-btn'
					className='w-full'
					variant='gradient'
					onClick={onClick}
				>
					{t('actions:selectTimeSlot.btnLabel')}
				</Button>
			</div>
		</div>
	)
}
